import { useState } from 'react';
import classNames from 'classnames';
import Image from 'next/image';

import AutosuggestInput from 'components/autosuggestInput';
import ZpButton from 'components/zpButton';
import { AUTOSUGGEST_CATEGORIES } from 'utils/constants-v2';

import stylesSearchBar from './searchBar.module.css';

type AutoSuggestCategory = typeof AUTOSUGGEST_CATEGORIES[keyof typeof AUTOSUGGEST_CATEGORIES];

interface IProps {
  searchBars: {
    /* Category of autosuggestion among AUTOSUGGEST_CATEGORIES */
    category: AutoSuggestCategory;
    /* If true then the search will be applied instantly as soon 
        as the option is selected from the list of suggestions */
    submitOnSelect?: boolean;
    /* The value to be prefilled in the autosuggestion */
    prefillValue?: { displayValue: string; valueObj: any };
    /* List of classnames to be applied to the autosuggest */
    className?: string;
    /* Event handler for the change of autosuggest */
    onChange?: (searchString: string) => void;
    /* Event handler for the keydown event */
    onKeyDown?: (key: string) => void;
    /* Event handler on an error */
    onError?: (error: string | Error) => void;
    /* Other autosuggest props directly to be applied to autosuggest */
    otherAutosuggestProps?: {
      extended?: boolean;
      indexableOnly?: string;
    };
    /* Input props to be applied to input element of autosuggest */
    inputProps?: {
      className?: string;
      id?: string;
      name?: string;
      placeholder?: string;
    };
  }[];
  isMobile?: boolean;
  /* The className for form */
  formClassName?: string;
  /* The className for submit button  */
  submitBtnClassName?: string;
  /* Event handler for the click on the search button */
  onSubmit?: (value: {
    [category: AutoSuggestCategory]: { displayValue: string; valueObj: any };
  }) => void;
}

const HeaderSearchBar = (props: IProps) => {
  const { searchBars, submitBtnClassName, onSubmit, formClassName, isMobile } = props;

  const [searchBarValue, setSearchBarValue] = useState(
    searchBars.reduce(
      (prev, curr) => ({
        ...prev,
        [curr.category]: curr?.prefillValue || {},
      }),
      {}
    )
  );

  const handleChange =
    (category: AutoSuggestCategory, submitOnSelect: boolean) => (inputElement: any) => {
      if (inputElement.suggestionSelected) {
        const newValue = {
          ...searchBarValue,
          [category]: inputElement,
        };
        if (submitOnSelect) onSubmit(newValue);
        setSearchBarValue(newValue);
      }
    };

  return (
    <div
      {...(isMobile
        ? {
            role: 'tabpanel',
            className: classNames(stylesSearchBar.searchBarFieldset),
            id: 'careers',
          }
        : {})}
    >
      <form
        className={
          formClassName ||
          classNames(
            isMobile ? 'form-inline w-100' : 'd-flex',
            isMobile ? stylesSearchBar.formInline : stylesSearchBar.container
          )
        }
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(searchBarValue);
        }}
      >
        {searchBars.map(
          (
            {
              category,
              submitOnSelect,
              inputProps,
              prefillValue,
              otherAutosuggestProps,
              className,
              onChange,
              onKeyDown,
              onError,
            },
            index
          ) => (
            <AutosuggestInput
              key={category}
              category={category}
              prefillValue={prefillValue?.displayValue}
              inputProps={{
                placeholder: inputProps.placeholder,
                className: classNames(
                  'typeahead',
                  { [stylesSearchBar.input]: !isMobile },
                  inputProps.className,
                  {
                    [stylesSearchBar.firstInput]: index === 0 && !isMobile,
                    'mt-2': index === 1 && isMobile,
                    'form-control': isMobile,
                  }
                ),
              }}
              onKeyDown={onKeyDown}
              onErrorCallback={onError}
              onChange={onChange || handleChange(category, submitOnSelect)}
              className={
                className ||
                classNames('w-100', stylesSearchBar.suggest, {
                  [stylesSearchBar.maxWidth190]: !isMobile,
                })
              }
              {...otherAutosuggestProps}
            />
          )
        )}
        <ZpButton
          theme="super-primary"
          size="big"
          type="submit"
          className={
            submitBtnClassName ||
            classNames(
              { [stylesSearchBar.button]: !isMobile },
              isMobile ? 'btn btn-orange' : 'z-p-0 custom-btn'
            )
          }
          withContent={false}
          isFullWidth={isMobile}
        >
          {isMobile ? (
            <i className="fa fa-search" />
          ) : (
            <Image src="/ui-router/images/new-search.svg" width={20} height={20} alt="Search" />
          )}
        </ZpButton>
      </form>
    </div>
  );
};

export default HeaderSearchBar;
